import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Divider
} from '@mui/material';
import {
  MoveUp as MoveGroupIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../../services/clinicalSupervisorApi';

function ChangeServiceGroupModal({ open, onClose, selectedClients, serviceGroups, token, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [targetServiceGroup, setTargetServiceGroup] = useState(null);
  const [therapistAction, setTherapistAction] = useState('keep'); // 'keep', 'unassign', or 'reassign'
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [therapistsInTargetGroup, setTherapistsInTargetGroup] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);

  useEffect(() => {
    if (open) {
      setError('');
      setWarning('');
      setTargetServiceGroup(null);
      setTherapistAction('keep');
      setSelectedTherapist(null);
      setTherapistsInTargetGroup([]);
    }
  }, [open]);

  useEffect(() => {
    if (targetServiceGroup) {
      checkTherapistCompatibility();
    }
  }, [targetServiceGroup, selectedClients]);

  const checkTherapistCompatibility = async () => {
    try {
      setLoadingTherapists(true);
      setWarning('');

      // Get therapists in the target service group
      const response = await clinicalSupervisorApi.getAssignedTherapists(true, token);
      const allTherapists = response.therapists || [];

      // Filter therapists who are in the target service group
      const therapistsInGroup = allTherapists.filter(therapist => {
        return therapist.serviceGroups?.some(
          sg => sg.groupId === targetServiceGroup._id && sg.isActive && sg.roles?.includes('therapist')
        );
      });

      setTherapistsInTargetGroup(therapistsInGroup);

      // Check if any selected clients have therapists NOT in target group
      const clientsWithIncompatibleTherapists = selectedClients.filter(client => {
        if (!client.assignedTherapist) return false; // Unassigned clients are fine

        const therapistId = client.assignedTherapist._id;
        const therapistInTargetGroup = therapistsInGroup.some(t => t._id === therapistId);

        return !therapistInTargetGroup;
      });

      if (clientsWithIncompatibleTherapists.length > 0) {
        setWarning(
          `${clientsWithIncompatibleTherapists.length} client(s) have therapists who are not in the target service group "${targetServiceGroup.name}". ` +
          `You must either unassign these clients or reassign them to a therapist in the target group.`
        );
        // Force user to make a choice
        setTherapistAction('unassign');
      } else {
        // All therapists are compatible, can keep assignments
        setTherapistAction('keep');
      }

    } catch (err) {
      console.error('Error checking therapist compatibility:', err);
      setError('Failed to load therapists for target service group');
    } finally {
      setLoadingTherapists(false);
    }
  };

  const handleMove = async () => {
    if (!targetServiceGroup) {
      setError('Please select a target service group');
      return;
    }

    if (selectedClients.length === 0) {
      setError('No clients selected');
      return;
    }

    if (therapistAction === 'reassign' && !selectedTherapist) {
      setError('Please select a therapist for reassignment');
      return;
    }

    // Check if reassignment therapist has capacity
    if (therapistAction === 'reassign') {
      const currentCount = selectedTherapist.therapistInfo?.assignedClientCount || 0;
      const maxCapacity = selectedTherapist.therapistInfo?.maxClientCapacity || 20;
      const availableSlots = maxCapacity - currentCount;

      const clientsNeedingReassignment = selectedClients.filter(c => {
        if (!c.assignedTherapist) return false;
        const therapistInTargetGroup = therapistsInTargetGroup.some(t => t._id === c.assignedTherapist._id);
        return !therapistInTargetGroup;
      });

      if (clientsNeedingReassignment.length > availableSlots) {
        setError(`Therapist only has ${availableSlots} available slots. ${clientsNeedingReassignment.length} clients need reassignment.`);
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      // Step 1: Move clients to new service group
      await clinicalSupervisorApi.changeClientServiceGroup(
        selectedClients.map(c => c._id),
        targetServiceGroup._id,
        token
      );

      // Step 2: Handle therapist assignments based on user choice
      if (therapistAction === 'unassign') {
        // Unassign clients whose therapists are not in target group
        const clientsToUnassign = selectedClients.filter(c => {
          if (!c.assignedTherapist) return false;
          const therapistInTargetGroup = therapistsInTargetGroup.some(t => t._id === c.assignedTherapist._id);
          return !therapistInTargetGroup;
        });

        if (clientsToUnassign.length > 0) {
          await clinicalSupervisorApi.unassignClients(
            clientsToUnassign.map(c => c._id),
            token
          );
        }

      } else if (therapistAction === 'reassign') {
        // Reassign clients whose therapists are not in target group
        const clientsToReassign = selectedClients.filter(c => {
          if (!c.assignedTherapist) return false;
          const therapistInTargetGroup = therapistsInTargetGroup.some(t => t._id === c.assignedTherapist._id);
          return !therapistInTargetGroup;
        });

        if (clientsToReassign.length > 0) {
          await clinicalSupervisorApi.assignClientsToTherapist(
            clientsToReassign.map(c => c._id),
            selectedTherapist._id,
            token
          );
        }
      }
      // If 'keep', do nothing - clients keep their current therapists (who are already in target group)

      onSuccess(
        `Successfully moved ${selectedClients.length} client(s) to ${targetServiceGroup.name}` +
        (therapistAction === 'unassign' ? ' (incompatible therapist assignments removed)' : '') +
        (therapistAction === 'reassign' ? ` and reassigned to ${selectedTherapist.name}` : '')
      );
      onClose();
    } catch (err) {
      console.error('Error changing service group:', err);
      setError(err.response?.data?.msg || 'Failed to move clients');
    } finally {
      setLoading(false);
    }
  };

  const getClientServiceGroup = (client) => {
    const activeGroup = client.serviceGroups?.find(sg => sg.isActive && sg.roles?.includes('client'));
    return activeGroup?.groupId?.name || 'Unknown';
  };

  // Check if warning exists (incompatible therapists)
  const hasIncompatibleTherapists = warning.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MoveGroupIcon />
          Move Clients to Different Service Group
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}

        {/* Selected Clients Summary */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Clients ({selectedClients.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {selectedClients.slice(0, 5).map(client => (
              <Chip
                key={client._id}
                label={`${client.name} (${getClientServiceGroup(client)} → ${client.assignedTherapist?.name || 'Unassigned'})`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {selectedClients.length > 5 && (
              <Chip
                label={`+${selectedClients.length - 5} more`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </Paper>

        {/* Target Service Group Selection */}
        <Autocomplete
          value={targetServiceGroup}
          onChange={(e, newValue) => setTargetServiceGroup(newValue)}
          options={serviceGroups}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Service Group"
              placeholder="Select service group to move clients to..."
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description || 'No description'}
                </Typography>
              </Box>
            </li>
          )}
        />

        {/* Therapist Assignment Handling */}
        {targetServiceGroup && !loadingTherapists && (
          <>
            <Divider sx={{ my: 3 }} />

            <FormControl component="fieldset">
              <FormLabel component="legend">
                Handle Current Therapist Assignments:
              </FormLabel>
              <RadioGroup
                value={therapistAction}
                onChange={(e) => {
                  setTherapistAction(e.target.value);
                  if (e.target.value !== 'reassign') {
                    setSelectedTherapist(null);
                  }
                }}
              >
                <FormControlLabel
                  value="keep"
                  control={<Radio />}
                  label="Keep current therapist assignments (only if therapists are in target group)"
                  disabled={hasIncompatibleTherapists}
                />
                <FormControlLabel
                  value="unassign"
                  control={<Radio />}
                  label="Unassign clients whose therapists are not in target service group"
                />
                <FormControlLabel
                  value="reassign"
                  control={<Radio />}
                  label="Reassign clients to a therapist in the target service group"
                />
              </RadioGroup>
            </FormControl>

            {/* Therapist Selection for Reassignment */}
            {therapistAction === 'reassign' && (
              <Autocomplete
                value={selectedTherapist}
                onChange={(e, newValue) => setSelectedTherapist(newValue)}
                options={therapistsInTargetGroup}
                getOptionLabel={(option) =>
                  `${option.name} - ${option.therapistInfo?.assignedClientCount || 0}/${option.therapistInfo?.maxClientCapacity || 20} clients`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Therapist in Target Group"
                    placeholder="Choose therapist..."
                    sx={{ mt: 2 }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email} • {option.therapistInfo?.assignedClientCount || 0}/{option.therapistInfo?.maxClientCapacity || 20} clients
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText={`No therapists found in ${targetServiceGroup.name}`}
              />
            )}

            {therapistsInTargetGroup.length === 0 && therapistAction === 'reassign' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No therapists are currently assigned to {targetServiceGroup.name}. You must unassign clients or add therapists to the target group first.
              </Alert>
            )}
          </>
        )}

        {loadingTherapists && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant="contained"
          disabled={
            loading ||
            !targetServiceGroup ||
            loadingTherapists ||
            (therapistAction === 'reassign' && !selectedTherapist)
          }
          startIcon={loading ? <CircularProgress size={20} /> : <MoveGroupIcon />}
        >
          {loading ? 'Moving...' : `Move ${selectedClients.length} Client${selectedClients.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeServiceGroupModal;
